import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";

/**
 * Per-user override over role widget defaults.
 *
 * Delta model: a row exists only for widgets the admin has explicitly
 * customised for that user. Missing rows ⇒ role default applies.
 *
 * - `visible=false` hides a widget that the role would otherwise show
 * - `display_order` overrides the role-default order for that widget
 * - `config` is reserved for per-user widget config (not implemented yet)
 */
export interface UserWidgetOverride {
  id: string;
  user_id: string;
  widget_id: string;
  visible: boolean;
  display_order: number | null;
}

const QUERY_KEY = ["user-widget-overrides"];

/** Fetch overrides for a single user. Empty array if none. */
export function useUserWidgetOverrides(userId: string | null | undefined) {
  return useQuery({
    queryKey: [...QUERY_KEY, userId],
    enabled: !!userId,
    queryFn: async (): Promise<UserWidgetOverride[]> => {
      const { data, error } = await supabase
        .from("user_widget_overrides")
        .select("id, user_id, widget_id, visible, display_order")
        .eq("user_id", userId!);
      if (error) throw error;
      return (data ?? []) as UserWidgetOverride[];
    },
    staleTime: 60_000,
  });
}

/** Upsert (insert or update) an override. Controls visible + display_order. */
export function useUpsertUserWidgetOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      user_id: string;
      widget_id: string;
      visible: boolean;
      display_order?: number | null;
    }) => {
      const { error } = await supabase
        .from("user_widget_overrides")
        .upsert(
          {
            user_id: params.user_id,
            widget_id: params.widget_id,
            visible: params.visible,
            display_order: params.display_order ?? null,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id,widget_id" },
        );
      if (error) throw error;
    },
    onSuccess: async (_, variables) => {
      await invalidateFor(qc, "user_widget_overrides", { userId: variables.user_id });
    },
  });
}

/** Delete an override row (resets that widget back to role default). */
export function useDeleteUserWidgetOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { user_id: string; widget_id: string }) => {
      const { error } = await supabase
        .from("user_widget_overrides")
        .delete()
        .eq("user_id", params.user_id)
        .eq("widget_id", params.widget_id);
      if (error) throw error;
    },
    onSuccess: async (_, variables) => {
      await invalidateFor(qc, "user_widget_overrides", { userId: variables.user_id });
    },
  });
}
